import { useState, useEffect } from "react";
import classes from "./TitleDescription.module.css";
import ErrorMessage from "../../StudioErstellen/ErrorMessage";
import { useStudioEditor } from '../../../store/studioEditorStore';

const DESCRIPTION_MIN_LENGTH = 10;
const DESCRIPTION_MIN_LETTERS = 8;
const DESCRIPTION_MAX_LENGTH = 1500;

export const TitleDescription = ({ validate, studio }) => {
    const { updateField, updatedFields } = useStudioEditor();
    const hasUpdatedField = (fieldName) => Object.prototype.hasOwnProperty.call(updatedFields, fieldName);
    const getUpdatedField = (fieldName, fallback) => (
        hasUpdatedField(fieldName) ? updatedFields[fieldName] : fallback
    );
    const [text, setText] = useState(getUpdatedField('StudioName', studio?.StudioName || ''));
    const [beschreibung, setBeschreibung] = useState(getUpdatedField('description', studio?.description || ''));
    const [errors, setErrors] = useState({ StudioName: "", description: "" });

    useEffect(() => {
        if (studio) {
            setText(getUpdatedField('StudioName', studio.StudioName || ''));
            setBeschreibung(getUpdatedField('description', studio.description || ''));
        }
        // Rehydrate from updatedFields on remount; user input updates local state directly.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [studio]);

    const handleValidation = () => {
        const newErrors = { StudioName: "", description: "" };

        const titleLetterCount = (text.match(/[a-zA-ZäöüÄÖÜß]/g) || []).length;

        if (text.trim() === "") {
            newErrors.StudioName = "Titel muss ausgefüllt werden";
        } else if (/^\d+$/.test(text.trim())) {
            newErrors.StudioName = "Titel darf nicht nur aus Zahlen bestehen";
        } else if (titleLetterCount < 6) {
            newErrors.StudioName = "Titel muss mindestens 6 Buchstaben enthalten";
        }

        if (beschreibung.trim() === "") {
            newErrors.description = "Beschreibung muss ausgefüllt werden";
        } else if (/^\d+$/.test(beschreibung.trim())) {
            newErrors.description = "Beschreibung darf nicht nur aus Zahlen bestehen";
        } else {
            const descriptionLetterCount = (beschreibung.match(/[a-zA-ZäöüÄÖÜß]/g) || []).length;
            if (descriptionLetterCount < DESCRIPTION_MIN_LETTERS) {
                newErrors.description = `Beschreibung muss mindestens ${DESCRIPTION_MIN_LETTERS} Buchstaben enthalten`;
            } else if (beschreibung.length < DESCRIPTION_MIN_LENGTH) {
                newErrors.description = `Beschreibung muss mindestens ${DESCRIPTION_MIN_LENGTH} Zeichen haben`;
            } else if (beschreibung.length > DESCRIPTION_MAX_LENGTH) {
                newErrors.description = `Beschreibung darf maximal ${DESCRIPTION_MAX_LENGTH} Zeichen haben`;
            }
        }

        setErrors(newErrors);

        return !newErrors.StudioName && !newErrors.description;
    };

    const handleTitleChange = (event) => {
        const inputValue = event.target.value;
        if (inputValue.length <= 40) {
            setText(inputValue);
            setErrors((prev) => ({ ...prev, StudioName: "" }));
            updateField('StudioName', inputValue);
        }
    };

    const handleDescriptionChange = (event) => {
        const inputValue = event.target.value;
        if (inputValue.length <= DESCRIPTION_MAX_LENGTH) {
            setBeschreibung(inputValue);
            setErrors((prev) => ({ ...prev, description: "" }));
            updateField('description', inputValue);
        }
    };

    useEffect(() => {
        validate.current = handleValidation;
    }, [text, beschreibung, validate]);

    return (
    <div className={classes.mainContainer}>
        <div className={classes.container}>
            <h2 className={`${classes.title} titleSemibold2`}>Titel</h2>
            <div className={classes.inputContainer}>
                <textarea
                    type="text"
                    className={`${classes.input} slimRegularLargeText`}
                    placeholder="Beispiel: Industrielles Fotostudio mit Tageslicht"
                    value={text}
                    onChange={handleTitleChange}
                />
                <div className={`${classes.textLength} slimRegularSmallText`}>
                    <p>{text.length}/40</p>
                    {errors.StudioName && <ErrorMessage>{errors.StudioName}</ErrorMessage>}
                </div>
            </div>
        </div>
        <div className={classes.container}>
            <h2 className={`${classes.title} titleSemibold2`}>Beschreibung</h2>
            <div className={classes.inputContainer}>
                <textarea
                    type="text"
                    className={`${classes.input} slimRegularLargeText`}
                    placeholder="Beispiel: Industrielles Fotostudio mit Tageslicht"
                    value={beschreibung}
                    onChange={handleDescriptionChange}
                />
                <div className={`${classes.textLength} slimRegularSmallText`}>
                    <p>{beschreibung.length}/{DESCRIPTION_MAX_LENGTH}</p>
                    {errors.description && <ErrorMessage>{errors.description}</ErrorMessage>}
                </div>
            </div>
        </div>
    </div>
    )
};

export default TitleDescription;
